<template>
  <v-navigation-drawer
    v-model="internalDrawerOpen"
    app
    right
    temporary
    width="400"
    class="column-drawer"
  >
    <!-- Drawer Header -->
    <v-toolbar
      flat
      class="column-drawer-header"
    >
      <v-toolbar-title class="d-flex align-center flex-column align-start">
        <div class="d-flex align-center">
          <font-awesome-icon
            :icon="['fas', 'columns']"
            class="mr-2"
          />
          Columns
        </div>
        <div
          v-if="viewContext"
          class="caption grey--text mt-1"
        >
          {{ viewContext }}
        </div>
      </v-toolbar-title>
      <v-spacer />
      <v-btn
        icon
        @click="close"
      >
        <font-awesome-icon :icon="['fas', 'times']" />
      </v-btn>
    </v-toolbar>

    <v-divider />

    <!-- Drawer Content -->
    <v-container class="column-drawer-content pa-4">
      <!-- Help Section -->
      <v-card
        outlined
        class="mb-4"
      >
        <v-card-text class="py-2">
          <div class="d-flex align-center mb-2">
            <font-awesome-icon
              :icon="['fas', 'info-circle']"
              class="mr-2 grey--text"
              size="sm"
            />
            <span class="caption font-weight-medium">Column Controls</span>
          </div>

          <div class="caption grey--text text--darken-1">
            <div class="d-flex align-start mb-1">
              <font-awesome-icon
                :icon="['fas', 'grip-dots-vertical']"
                class="mr-2 mt-1"
                size="xs"
              />
              <span>Drag columns to reorder them</span>
            </div>
            <div class="d-flex align-start mb-1">
              <font-awesome-icon
                :icon="['fas', 'thumbtack']"
                class="mr-2 mt-1"
                size="xs"
              />
              <span>Pin columns to freeze them in place</span>
            </div>
            <div class="d-flex align-start">
              <font-awesome-icon
                :icon="['fas', 'eye']"
                class="mr-2 mt-1"
                size="xs"
              />
              <span>Toggle visibility with checkboxes</span>
            </div>
          </div>
        </v-card-text>
      </v-card>

      <!-- Column List -->
      <v-list
        v-if="orderedColumns && orderedColumns.length > 0"
        class="pa-0"
      >
        <draggable
          v-model="orderedColumns"
          handle=".drag-handle"
        >
          <v-list-item
            v-for="(column, colIndex) of orderedColumns"
            :key="column.key || colIndex"
            class="px-2 column-list-item"
          >
            <v-list-item-icon class="mr-3 my-2">
              <font-awesome-icon
                :icon="['fas', 'grip-dots-vertical']"
                class="drag-handle cursor-grab grey--text"
                :title="`Drag ${getColumnLabel(column)}`"
              />
            </v-list-item-icon>

            <v-list-item-content class="py-2">
              <v-list-item-title class="d-flex align-center">
                <span class="font-weight-medium">{{ getColumnLabel(column) }}</span>
                <v-chip
                  v-if="column.required"
                  x-small
                  color="grey lighten-2"
                  class="ml-2"
                >
                  Required
                </v-chip>
              </v-list-item-title>
              <v-list-item-subtitle
                v-if="column.tooltip"
                class="caption mt-1"
              >
                {{ column.tooltip }}
              </v-list-item-subtitle>
            </v-list-item-content>

            <v-list-item-action class="my-2 ml-2">
              <div class="d-flex align-center flex-wrap justify-end">
                <!-- Pin/Freeze Button -->
                <v-btn
                  x-small
                  text
                  class="text-none mr-1"
                  :color="column.frozen ? 'primary' : 'grey'"
                  :title="`${column.frozen ? 'Unpin' : 'Pin'} ${getColumnLabel(column)}`"
                  @click="freezeColumn(column)"
                >
                  <font-awesome-icon
                    :icon="['fas', 'thumbtack']"
                    size="xs"
                    class="mr-1"
                  />
                  {{ column.frozen ? 'Unpin' : 'Pin' }}
                </v-btn>

                <!-- Visibility Toggle -->
                <v-btn
                  x-small
                  text
                  class="text-none"
                  :color="column.visible && !column.required ? 'primary' : 'grey'"
                  :disabled="column.required"
                  :title="`${column.visible ? 'Hide' : 'Show'} ${getColumnLabel(column)}`"
                  @click="toggleColumn(column)"
                >
                  <font-awesome-icon
                    :icon="column.visible ? ['fas', 'eye'] : ['fas', 'eye-slash']"
                    size="xs"
                    class="mr-1"
                  />
                  {{ column.visible ? 'Hide' : 'Show' }}
                </v-btn>
              </div>
            </v-list-item-action>
          </v-list-item>
        </draggable>
      </v-list>

      <!-- Empty State -->
      <v-alert
        v-else
        type="info"
        text
        class="mt-4"
      >
        <div class="d-flex align-center">
          <font-awesome-icon
            :icon="['fas', 'info-circle']"
            class="mr-2"
          />
          <span>No columns available. Please check your table configuration.</span>
        </div>
      </v-alert>
    </v-container>

    <!-- Drawer Footer -->
    <v-divider />
    <v-card-actions class="pa-4">
      <v-btn
        outlined
        class="text-none"
        @click="resetColumns"
      >
        <font-awesome-icon
          :icon="['fas', 'undo']"
          size="sm"
          class="mr-2"
        />
        Reset to Default
      </v-btn>
      <v-spacer />
      <v-btn
        color="primary"
        class="text-none"
        @click="close"
      >
        Done
      </v-btn>
    </v-card-actions>
  </v-navigation-drawer>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
import draggable from 'vuedraggable'
import columnUtils from '@/tools/store/utils/columns'

const { mapGetters: mapToolsGetters } = createNamespacedHelpers('tools')

export default {
  name: 'ColumnDrawer',
  components: { draggable },
  props: {
    drawerOpen: {
      type: Boolean,
      default: false,
    },
    currentRequiredColumns: {
      type: Array,
      default: () => [],
    },
    reportKey: {
      type: String,
      default: undefined,
    },
    tableColumns: {
      type: Array,
      default: () => [],
    },
    viewContext: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      internalDrawerOpen: this.drawerOpen,
    }
  },
  computed: {
    ...mapToolsGetters(['frozenColumns']),

    orderedColumns: {
      get() {
        return columnUtils.normalizeColumns(this.tableColumns, this.currentRequiredColumns)
      },
      set(value) {
        this.$store.commit('tools/columns', { [this.reportKey]: value })
      },
    },
  },
  watch: {
    drawerOpen(newValue) {
      this.internalDrawerOpen = newValue
    },
    internalDrawerOpen(newValue) {
      if (!newValue) {
        this.$emit('close')
      }
    },
  },
  methods: {
    close() {
      this.internalDrawerOpen = false
      this.$emit('close')
    },
    getColumnLabel(column) {
      return columnUtils.getColumnLabel(column)
    },
    getColumnIdentifier(column) {
      return columnUtils.getColumnKey(column)
    },
    findColumnIndex(column) {
      const columnIdentifier = this.getColumnIdentifier(column)

      if (!columnIdentifier) return -1

      return (this.tableColumns || []).findIndex((currentColumn) => this.getColumnIdentifier(currentColumn) === columnIdentifier)
    },
    freezeColumn(column) {
      const columnIndex = this.findColumnIndex(column)
      const tableColumns = this.tableColumns || []

      this.$store.dispatch('tools/freezeColumn', { columnIndex, reportKey: this.reportKey, tableColumns })
    },
    toggleColumn(column) {
      const { currentRequiredColumns, tableColumns, reportKey } = this
      const columnIndex = this.findColumnIndex(column)

      if (columnIndex === -1) return false

      const newColumns = [...tableColumns]
      const currentColumn = newColumns[columnIndex]
      const isRequiredColumn = currentRequiredColumns?.length && currentRequiredColumns.indexOf(currentColumn.key) !== -1

      if (isRequiredColumn) {
        const message = 'Cannot Hide Required Column'

        this.$store.commit('tools/snackbar', true)
        this.$store.commit('tools/snack', { message, status: 'error' })

        return false
      }

      newColumns[columnIndex] = {
        ...currentColumn,
        visible: currentColumn.visible === false,
      }

      this.$store.commit('tools/columns', { [reportKey]: newColumns })

      return newColumns
    },
    resetColumns() {
      // Emit event to parent to handle reset logic
      this.$emit('reset')
    },
  },
}
</script>

<style scoped lang="scss">
.column-drawer {
  ::v-deep .v-navigation-drawer__content {
    display: flex;
    flex-direction: column;
  }
}

.column-drawer-content {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
}

.column-drawer-content::-webkit-scrollbar {
  width: 8px;
}

.column-drawer-content::-webkit-scrollbar-track {
  background: transparent;
}

.column-drawer-content::-webkit-scrollbar-thumb {
  background: #bdbdbd;
  border-radius: 4px;
}

.column-drawer-content::-webkit-scrollbar-thumb:hover {
  background: #9e9e9e;
}

.column-list-item {
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.column-list-item:hover {
  background-color: rgba(0, 0, 0, 0.02);
}

.theme--dark .column-list-item {
  border-bottom-color: rgba(255, 255, 255, 0.06);
}

.theme--dark .column-list-item:hover {
  background-color: rgba(255, 255, 255, 0.04);
}

.drag-handle {
  cursor: grab;
}

.drag-handle:active {
  cursor: grabbing;
}
</style>
